import React from "react";
import { useEffect, useState } from "react";

// type LoadingType = "spinner" | "dots" | "skeleton" | "pulse"
// type LoadingSize = "sm" | "md" | "lg"
// type LoadingColor = "primary" | "secondary" | "success" | "error" | "warning" | "info"

// interface LoadingProps {
//   type?: LoadingType
//   size?: LoadingSize
//   color?: LoadingColor
//   text?: string
//   fullScreen?: boolean
//   className?: string
// }

export function Loading({
  type = "spinner",
  size = "md",
  color = "primary",
  text,
  fullScreen = false,
  className = "",
}) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  // Size classes
  const sizeClasses = {
    spinner: {
      sm: "w-4 h-4",
      md: "w-8 h-8",
      lg: "w-12 h-12",
    },
    dots: {
      sm: "h-1 w-1",
      md: "h-2 w-2",
      lg: "h-3 w-3",
    },
    skeleton: {
      sm: "h-4",
      md: "h-6",
      lg: "h-8",
    },
    pulse: {
      sm: "h-4 w-4",
      md: "h-8 w-8",
      lg: "h-12 w-12",
    },
  };

  // Color classes
  const colorClasses = {
    spinner: {
      primary: "border-blue-500",
      secondary: "border-gray-500",
      success: "border-green-500",
      error: "border-red-500",
      warning: "border-yellow-500",
      info: "border-cyan-500",
    },
    dots: {
      primary: "bg-blue-500",
      secondary: "bg-gray-500",
      success: "bg-green-500",
      error: "bg-red-500",
      warning: "bg-yellow-500",
      info: "bg-cyan-500",
    },
    skeleton: {
      primary: "bg-blue-200",
      secondary: "bg-gray-200",
      success: "bg-green-200",
      error: "bg-red-200",
      warning: "bg-yellow-200",
      info: "bg-cyan-200",
    },
    pulse: {
      primary: "bg-blue-500",
      secondary: "bg-gray-500",
      success: "bg-green-500",
      error: "bg-red-500",
      warning: "bg-yellow-500",
      info: "bg-cyan-500",
    },
  };

  // Container classes
  const containerClasses = fullScreen
    ? "fixed inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 z-50"
    : "flex items-center justify-center";

  // Render the appropriate loading indicator based on type
  const renderLoadingIndicator = () => {
    switch (type) {
      case "spinner":
        return (
          <div
            className={`${sizeClasses.spinner[size]} border-4 border-t-transparent rounded-full animate-spin ${colorClasses.spinner[color]}`}
            role="status"
            aria-label="Loading"
          />
        );
      case "dots":
        return (
          <div className="flex space-x-2" role="status" aria-label="Loading">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={`${sizeClasses.dots[size]} ${colorClasses.dots[color]} rounded-full animate-bounce`}
                style={{ animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </div>
        );
      case "skeleton":
        return (
          <div
            role="status"
            className="w-full max-w-md animate-pulse"
            aria-label="Loading"
          >
            <div
              className={`${sizeClasses.skeleton[size]} ${colorClasses.skeleton[color]} rounded-md mb-2.5`}
            />
            <div
              className={`${sizeClasses.skeleton[size]} ${colorClasses.skeleton[color]} rounded-md mb-2.5 w-3/4`}
            />
            <div
              className={`${sizeClasses.skeleton[size]} ${colorClasses.skeleton[color]} rounded-md w-1/2`}
            />
          </div>
        );
      case "pulse":
        return (
          <div
            className={`${sizeClasses.pulse[size]} ${colorClasses.pulse[color]} rounded-full animate-pulse`}
            role="status"
            aria-label="Loading"
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`${containerClasses} ${className}`}>
      <div className="flex flex-col items-center">
        {renderLoadingIndicator()}
        {text && (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {text}
          </p>
        )}
      </div>
    </div>
  );
}
